import {Input, Modal, Spin} from "antd";
import React, {useRef, useState} from "react";
import Captcha, {CaptchaCallableFunctions} from "../captcha/Captcha";
import {CheckCircleFilled, CloseCircleOutlined} from "@ant-design/icons";
import TfButton from "../../tf-button/TfButton";
import {CONFIG} from "../../../util/config";

type UserInfo = {
    email: string,
    captcha: string,
    captchaAt: number | null
}


const resetPasswordForEmail  = ({captcha, captchaAt, email}: {captcha: string, captchaAt: number | null, email: string}) : Promise<void> => {

    return fetch(CONFIG.restPrefix + '/login/forgotten-password', {
        method: 'POST',
        body: JSON.stringify({
            captcha,
            captchaAt,
            email
        }),
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        ...(CONFIG.isDev && { credentials: 'include' })
    })
        .then(response => {
            if (response.status !== 200) {
                throw new Error()
            }
        })

}

const ResetPasswordDialog = ({onClose}: {onClose: () => void}) => {

    const [sendStatus, setSendStatus] = useState<"START" | "SENDING" | "SENT" | "ERROR">("START");
    const [userInfo, setUserInfo] = useState<UserInfo>({
        email: "",
        captcha: "",
        captchaAt: null
    });

    const captchaRef = useRef<CaptchaCallableFunctions>(null);

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const email = event.target.value
        setUserInfo(prev => ({
            ...prev,
            email: email || ""
        }));
    }

    const handleCaptchaChange = (token: string | null, tokenAt: number | null) => {
        setUserInfo(prev => ({
            ...prev,
            captcha: token || "",
            captchaAt: tokenAt
        }));
    };

    const onSubmit = () => {
        setSendStatus("SENDING")
        resetPasswordForEmail(userInfo)
            .then(() => setSendStatus("SENT"))
            .catch(() => setSendStatus("ERROR"))
    }

    const disableSubmit = Object.values(userInfo).some(value => value === "" || value === null)

    const loadPage = () => {
        if (sendStatus === "START") {
            return <>
                <div>
                    <p>If you have forgotten your password, please enter your username, press the 'Reset' button and we will send a temporary password to your email address.</p>
                    <p>You will then have to update your password at the next login.</p>
                </div>

                <div style={{ display: "flex", justifyContent: "center" }}>
                    <div style={{ display: "flex",width: "304px", flexDirection: "column", gap: "16px" }}>
                        <Input style={{}} name={"email"} placeholder={"Username or Email"} onChange={handleEmailChange} />
                        <Captcha  onChange={handleCaptchaChange} ref={captchaRef} />
                        <TfButton disabled={disableSubmit} onClick={onSubmit}>Reset</TfButton>
                    </div>
                </div>
            </>
        }
        if (sendStatus === "SENDING") {
            return <>
                <div style={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                    <Spin/>
                    <span>Sending email...</span>
                </div>
            </>
        }
        if (sendStatus === "SENT") {
            return <>
                <div style={{ display: "flex", gap: '16px', alignItems: "center"}}>
                    <CheckCircleFilled style={{color: "green", fontSize: "24px"}}/>
                    <p style={{ margin: 0}}>If the account exists in the system, you will receive an email with the instructions to reset your password</p>
                </div>
            </>
        }
        if (sendStatus === "ERROR") {
            return <>
                <div style={{ display: "flex", gap: '16px', alignItems: "center"}}>
                    <CloseCircleOutlined style={{color: "red", fontSize: "24px"}}/>
                    <p style={{ margin: 0}}>There was an error sending a reset password to your email. For assistance please contact <a href="mailto:support@healthiq.co.uk">
                        <strong>Vantage Support team</strong>
                    </a></p>
                </div>
            </>
        }
    }

    return (
        <Modal
            title="Forgotten your password?"
            open={true}
            width={500}
            onCancel={onClose}
            data-testid="password-reset-modal"
            footer={null}
        >
            {loadPage()}
        </Modal>
    );
}

export default ResetPasswordDialog;